import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import AudioVisualizer from './AudioVisualizer';
import { db } from '../firebase';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { setShapeColorMode } from './AudioVisualizer/ShapeManager';

const App = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLooping, setIsLooping] = useState(true);
  const [audioFile, setAudioFile] = useState(null);
  const [colorMode, setColorMode] = useState('colorful');
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const isInitializedRef = useRef(false);
  const fileInputRef = useRef();

  // AudioContextのクリーンアップ
  const cleanupAudio = () => {
    const audioElement = audioRef.current;
    if (audioElement) {
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      audioElement.pause();
      audioElement.src = '';
      audioElement.load();
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    isInitializedRef.current = false;
  };

  const initializeAudioContext = () => {
    if (!audioContextRef.current && !isInitializedRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      isInitializedRef.current = true;
    }
  };

  // ファイル選択時の処理
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    cleanupAudio();
    initializeAudioContext();
    setAudioFile(null);
    setTimeout(() => {
      const url = URL.createObjectURL(file);
      setAudioFile(url);
      setIsPlaying(false);
    }, 0);
    e.target.value = "";
  };

  // audioFileが変わったタイミングでaudio要素とAudioContextを再接続
  useEffect(() => {
    if (!audioFile || !audioRef.current || !audioContextRef.current) return;
    try {
      const audioContext = audioContextRef.current;
      const audioElement = audioRef.current;
      const source = audioContext.createMediaElementSource(audioElement);
      sourceRef.current = source;
      source.connect(analyserRef.current);
      analyserRef.current.connect(audioContext.destination);
    } catch (error) {
      console.error('接続エラー:', error);
      cleanupAudio();
    }
  }, [audioFile]);

  // カラーモード切替時にShapeManagerへ反映
  useEffect(() => {
    setShapeColorMode(colorMode);
  }, [colorMode]);

  // Firebaseの状態を保存
  const saveState = async () => {
    try {
      const stateRef = collection(db, 'visualizer-states');
      await addDoc(stateRef, {
        isPlaying,
        isLooping,
        audioFile: audioFile || null,
        timestamp: new Date()
      });
      console.log('状態を保存しました！');
    } catch (error) {
      console.error('状態の保存に失敗しました:', error);
    }
  };

  // Firebaseの状態を読み込み
  const loadState = async () => {
    try {
      const stateRef = collection(db, 'visualizer-states');
      const q = query(stateRef, where("timestamp", ">", new Date(Date.now() - 24 * 60 * 60 * 1000)));
      const querySnapshot = await getDocs(q);
      const states = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      if (states.length > 0) {
        const latestState = states.sort((a, b) => b.timestamp - a.timestamp)[0];
        console.log('最新の状態を読み込みました:', latestState);
        setIsPlaying(latestState.isPlaying);
        setIsLooping(latestState.isLooping);
      }
    } catch (error) {
      console.error('状態の読み込みに失敗しました:', error);
    }
  };

  useEffect(() => {
    loadState();
  }, []);

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    const audioElement = audioRef.current;
    if (isPlaying) {
      audioElement.pause();
    } else {
      audioElement.play().catch(e => {
        console.error("再生失敗:", e);
      });
    }
    setIsPlaying(!isPlaying);
    saveState();
  };

  const handleLoopToggle = () => {
    const audioElement = audioRef.current;
    const nextLoop = !isLooping;
    setIsLooping(nextLoop);
    if (audioElement) {
      audioElement.loop = nextLoop;
    }
    saveState();
  };

  // カラーモード切替ボタンのハンドラ
  const handleColorModeToggle = () => {
    setColorMode((prev) => prev === 'colorful' ? 'cyber-green' : 'colorful');
  };

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return (
    <div className="app">
      <div className="controls">
        {audioFile && (
          <audio
            key={audioFile || 'empty'}
            ref={audioRef}
            src={audioFile}
            controls={false}
            autoPlay={false}
            preload="auto"
            loop={isLooping}
          />
        )}
        <div className="button-group">
          <div className="file-button">
            <input 
              type="file" 
              id="file-input" 
              accept=".mp3,.wav,.ogg" 
              onChange={handleFileChange} 
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            <label htmlFor="file-input" className="file-label">
              🎵 選択
            </label>
          </div>
          <button 
            onClick={handlePlayPause} 
            className="control-button play-button"
            disabled={!audioFile}
            title={isPlaying ? '一時停止' : '再生'}
          >
            {isPlaying ? '⏸️ 一時停止' : '▶️ 再生'}
          </button>
          <button
            className={`loop-button${isLooping ? ' active' : ''} control-button`}
            onClick={handleLoopToggle}
            aria-label={isLooping ? 'ループ' : '1回再生'}
          >
            🔁 {isLooping ? 'ループ' : '1回再生'}
          </button>
          <button
            onClick={handleColorModeToggle}
            className={`control-button color-mode-button`}
            title={colorMode === 'colorful' ? 'カラフルモード' : 'サイバーグリーンモード'}
          >
            {colorMode === 'colorful' ? '🌈 カラフル' : '🟢 サイバー'}
          </button>
        </div>
      </div>
      <div className="visualizer-container">
        {analyserRef.current && <AudioVisualizer analyser={analyserRef.current} />}
      </div>
    </div>
  );
};

export default App;